// Radha — the country filter (header) + the jurisdiction ribbon (top of main).
//
// The selector switches the viewing lens; the ribbon then SHOWS what that
// choice changed — regime name, withholding sections, limitation rule, default
// rail — pulled live from the pack tables rather than hardcoded marketing
// copy, so it can never claim a capability the packs don't actually carry.
import React from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { COUNTRIES, setViewCountry, useViewCountry, useIsOverridden } from "../lib/jurisdiction";
import { TranslationNote } from "./CurrencySwitcher";

export function CountrySwitcher({ tenantCountry }: { tenantCountry: string | null | undefined }) {
  const view = useViewCountry(tenantCountry);
  const overridden = useIsOverridden(tenantCountry);
  return (
    <select
      value={view}
      onChange={(e) => setViewCountry(e.target.value === (tenantCountry ?? "IN") ? null : e.target.value)}
      title="Country lens — switches which jurisdiction's rules, statutes and calendars every panel computes under. Your book and currency are unchanged."
      aria-label="Country filter"
      className="sanctuary-btn"
      style={{
        height: 30, padding: "0 8px", fontSize: 12.5, borderRadius: 9, cursor: "pointer",
        border: overridden ? "1px solid var(--accent-purple)" : "1px solid var(--border)",
        background: "var(--surface)", color: overridden ? "var(--accent-purple)" : "var(--fg-muted)",
        maxWidth: 150,
      }}
    >
      {COUNTRIES.map((c) => (
        <option key={c.code} value={c.code}>
          {c.label}{c.code === (tenantCountry ?? "IN") ? " (home)" : ""}
        </option>
      ))}
    </select>
  );
}

interface PackSummary {
  regime: { regime_name: string; model: string; clearance_id_label: string | null } | null;
  whtSections: number;
  whtTop: string | null;
  limitation: { years: number | null; statute_ref: string | null; verified: boolean } | null;
  rail: { label: string; settlement_days: number; instant: boolean } | null;
  legalRoutes: number;
}

function usePackSummary(country: string) {
  return useQuery({
    queryKey: ["pack-summary", country],
    queryFn: async (): Promise<PackSummary> => {
      const [regime, wht, lim, rail, legal] = await Promise.all([
        supabase.from("einvoice_regimes").select("regime_name, model, clearance_id_label").eq("country", country).maybeSingle(),
        supabase.from("withholding_rules").select("section, rate_pct").eq("country", country).gte("rate_pct", 0.1).order("rate_pct", { ascending: false }),
        supabase.from("limitation_rules").select("years, statute_ref, verified").eq("country", country).maybeSingle(),
        supabase.from("payment_rails").select("label, settlement_days, instant").eq("country", country).eq("is_default", true).maybeSingle(),
        supabase.from("legal_case_types").select("case_type", { count: "exact", head: true }).eq("country", country),
      ]);
      return {
        regime: regime.data ?? null,
        whtSections: (wht.data ?? []).length,
        whtTop: wht.data?.[0] ? `§${wht.data[0].section} @ ${Number(wht.data[0].rate_pct)}%` : null,
        limitation: lim.data ?? null,
        rail: rail.data ?? null,
        legalRoutes: legal.count ?? 0,
      };
    },
    staleTime: 600_000,
  });
}

function Chip({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <span style={{
      display: "inline-flex", gap: 5, alignItems: "baseline", padding: "3px 9px",
      borderRadius: 6, background: "var(--surface)", border: "1px solid var(--border)",
      fontSize: 11.5, whiteSpace: "nowrap",
    }}>
      <span style={{ color: "var(--fg-faint)" }}>{label}</span>
      <span style={{ color: muted ? "var(--fg-faint)" : "var(--fg)", fontWeight: muted ? 400 : 600 }}>{value}</span>
    </span>
  );
}

export function JurisdictionRibbon({ tenantCountry }: { tenantCountry: string | null | undefined }) {
  const view = useViewCountry(tenantCountry);
  const overridden = useIsOverridden(tenantCountry);
  const { data: s } = usePackSummary(view);
  const def = COUNTRIES.find((c) => c.code === view);

  if (!s) return null;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      padding: "7px 12px", margin: "0 0 14px", borderRadius: 9,
      border: overridden ? "1px solid var(--accent-purple)" : "1px solid var(--border)",
      background: overridden ? "var(--accent-purple-soft)" : "var(--surface-2)",
    }}>
      <span style={{ fontSize: 12, fontWeight: 600 }}>
        {def?.label}
        {overridden && <span style={{ color: "var(--accent-purple)", fontWeight: 500 }}> — viewing lens; your book is unchanged</span>}
      </span>
      <TranslationNote tenantCountry={tenantCountry} />
      <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginInlineStart: "auto" }}>
        <Chip label="e-invoicing" value={s.regime ? (s.regime.model === "none" ? "no mandate" : s.regime.regime_name) : "no pack"} muted={!s.regime || s.regime.model === "none"} />
        <Chip label="withholding" value={s.whtSections > 0 ? `${s.whtSections} sections · ${s.whtTop}` : "none"} muted={s.whtSections === 0} />
        <Chip label="limitation" value={s.limitation?.years ? `${s.limitation.years}y${s.limitation.verified ? "" : " (confirm)"}` : "state/case law"} muted={!s.limitation?.years} />
        <Chip label="default rail" value={s.rail ? `${s.rail.label}${s.rail.instant ? " · instant" : ` · D+${s.rail.settlement_days}`}` : "—"} muted={!s.rail} />
        <Chip label="legal routes" value={String(s.legalRoutes)} muted={s.legalRoutes === 0} />
      </span>
    </div>
  );
}
